/** Reading the job's gate into the four columns the Merge requests table has.
 *
 * The gate is a list, not a record, because which criteria were judged depends
 * on what was being judged: a circuit nobody has published yet is asked
 * whether its perimeter is complete, and a circuit that is already on the map
 * is asked instead whether this survey lost any of the coverage that is
 * already published (spec §6, and `src/domain/policy.ts` for the two lists).
 * The table has one Perimeter column for both of those questions, so a column
 * has to accept every name the criterion behind it goes by — a column that
 * matched only the first-publication spelling would print a dash on every
 * update, which reads as "not judged" when the truth is "judged and failed".
 *
 * Kept apart from the screen so the mapping can be tested without rendering a
 * table: the spellings are the whole of the logic, and they are the part that
 * drifts when the job grows a criterion.
 */

import { DASH, count } from '@/lib/format';
import type { GateCriterion, GateResult } from '@/lib/types';

/**
 * The four columns the design gives, and the criterion names each will accept.
 *
 * The job owns the spelling and more than one has been used. `perimeter_complete`
 * and `coverage_held` are not two spellings of one criterion — they are the
 * first-publication and update forms of the same column, and exactly one of
 * them is present on any given merge request.
 */
export const COLUMN_CRITERIA = {
  crossings: ['finish_crossings', 'crossings', 'finish'],
  layout: ['layout_agreement', 'layout', 'signature_match'],
  perimeter: ['perimeter_complete', 'coverage_held', 'perimeter', 'coverage'],
  kinds: ['kind_changes', 'kinds', 'quorum'],
} as const;

export type Column = keyof typeof COLUMN_CRITERIA;

/** Every criterion name any column would claim, so the leftovers can be found. */
const CLAIMED = new Set<string>(Object.values(COLUMN_CRITERIA).flat());

export function criterionFor(gate: GateResult, column: Column): GateCriterion | null {
  for (const name of COLUMN_CRITERIA[column]) {
    const found = gate.criteria.find((item) => item.name === name);
    if (found) return found;
  }
  return null;
}

/**
 * A criterion's value as a cell.
 *
 * The job writes whatever the criterion is measured in — a count, a distance,
 * a pair of percentages, a plain yes — so the only thing done here is to turn
 * a bare boolean into a word. `words` lets the layout column say "agree"
 * rather than "pass", which is what the criterion actually means.
 */
export function gateValue(
  item: GateCriterion | null,
  words: readonly [string, string] = ['pass', 'fail'],
  fallback: string = DASH,
): string {
  if (!item || item.value === null) return fallback;
  if (typeof item.value === 'boolean') return item.value ? words[0] : words[1];
  if (typeof item.value === 'number') return count(item.value);
  return item.value || fallback;
}

/** Amber for a criterion that failed; nothing for one that passed or was not
 *  judged. A failed criterion is not an error — it is the reason a pull
 *  request is waiting for somebody. */
export function gateColour(item: GateCriterion | null): string {
  if (!item || item.pass === null) return 'var(--color-neutral-400)';
  return item.pass ? 'var(--color-neutral-300)' : 'var(--color-warn)';
}

/** Whatever the job judged that no column shows, for the status cell's title. */
export function extraCriteria(gate: GateResult): string {
  return gate.criteria
    .filter((item) => !CLAIMED.has(item.name))
    .map((item) => `${item.name}: ${gateValue(item)}`)
    .join(' · ');
}
